import React, {useEffect, useMemo} from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  ModalFooter,
  Button,
  Grid,
  GridItem,
  AbsoluteCenter,
  Input,
  Box,
  useToast,
} from '@chakra-ui/react';
import Select from 'react-select';
import {useCreateFieldDefinition} from '../../../hooks/api/fieldDefinitions';
import {useCustomer} from '../../../hooks/api/metadata';
import {useGetAdAccounts} from '../../../hooks/api/linkedin';
import {IntentGroup, IntentGroupLabels} from '../../Intent/IntentModeStore';
import {
  LinkedInIntentFieldType,
  LinkedInIntentFieldTypes,
  IntentFieldInterval,
  generateIntentFieldName,
  LinkedInFieldTypeLabels,
} from '../../../shared/fields/intent';
import {ObjectId} from 'bson';
import {
  useForm,
  FormProvider,
  useFormContext,
  Controller,
} from 'react-hook-form';
import _ from 'lodash';
import {useIntentGroups} from './useIntentGroups';
import {errorSelectStyles} from '../../theme/select';

export const IntentFieldModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const intentGroups = useIntentGroups();

  return (
    <Modal isCentered={true} isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      {intentGroups.length > 0 ? (
        <IntentFieldModalContent onClose={onClose} />
      ) : (
        <ModalContent>
          <ModalHeader fontWeight="normal">Create Intent Field</ModalHeader>
          <ModalBody minH="200px">
            <AbsoluteCenter textColor="kgray.300">
              Please connect LinkedIn or add at least one intent source to
              create intent fields.
            </AbsoluteCenter>
          </ModalBody>
          <ModalCloseButton />
        </ModalContent>
      )}
    </Modal>
  );
};

type IntentFieldFormData = {
  group?: IntentGroup;
  interval?: IntentFieldInterval;
  fieldLabel: string;
  // LinkedIn intent specific fields
  linkedInField?: LinkedInIntentFieldType;
  adAccountId?: number;
  // Aggregated intent specific fields
  sourceId?: ObjectId;
};

const useIntentFieldForm = () => useFormContext<IntentFieldFormData>();

const IntentFieldModalContent = ({onClose}: {onClose: () => void}) => {
  const customer = useCustomer();
  const createFieldDefinition = useCreateFieldDefinition();
  const toast = useToast();

  const methods = useForm<IntentFieldFormData>({
    defaultValues: {
      fieldLabel: '',
    },
  });
  const {handleSubmit, getValues, setValue, watch} = methods;

  const [group, linkedInField, sourceId, interval] = watch([
    'group',
    'linkedInField',
    'sourceId',
    'interval',
  ]);

  // Auto-generate field name when dependencies change
  useEffect(() => {
    if (!group) {
      return;
    }

    const generatedName = generateIntentFieldName(
      group === 'linkedIn'
        ? {
            type: 'linkedIn',
            linkedInField,
            interval,
          }
        : {
            type: 'aggregated',
            sourceId,
            intentSources: customer.intentSources ?? [],
            interval,
          }
    );
    setValue('fieldLabel', generatedName);
  }, [
    customer.intentSources,
    group,
    linkedInField,
    interval,
    sourceId,
    setValue,
  ]);

  const saveIntentField = () => {
    if (!group || !interval) {
      return;
    }

    let sourceSpecificFields;
    if (group === 'linkedIn') {
      const adAccountId = getValues('adAccountId');
      if (!linkedInField || !adAccountId) {
        return;
      }

      sourceSpecificFields = {
        source: 'linkedIn',
        linkedInField,
        adAccountId,
      } as const;
    } else {
      if (!sourceId) {
        return;
      }

      sourceSpecificFields = {
        source: 'aggregated',
        sourceId: sourceId,
      } as const;
    }

    createFieldDefinition.mutate(
      {
        type: 'intent',
        interval,
        label: getValues('fieldLabel').trim(),
        dataType: 'number',
        status: 'published',
        ...sourceSpecificFields,
      },
      {
        onSuccess: () => {
          toast({
            description: 'Intent field created',
            status: 'success',
          });
          onClose();
        },
      }
    );
  };

  return (
    <ModalContent>
      <ModalHeader fontWeight="normal">
        <Text fontSize="xl">Create Intent Field</Text>
      </ModalHeader>
      <Box as="form" onSubmit={handleSubmit(saveIntentField)}>
        <ModalBody minH="300px">
          <FormProvider {...methods}>
            <Grid
              alignItems="baseline"
              columnGap={8}
              rowGap={4}
              templateColumns="150px 1fr"
            >
              <IntentSourceTypeRow />

              {group === 'linkedIn' && (
                <>
                  <LinkedInFieldRow />
                  <LinkedInAdAccountRow />
                </>
              )}

              {group && (
                <>
                  {group !== 'linkedIn' && <IntentSourceRow />}
                  <IntervalRow />
                  <FieldLabelRow />
                </>
              )}
            </Grid>
          </FormProvider>
        </ModalBody>
        <ModalFooter
          borderTop="1px solid"
          borderColor="kgray.200"
          justifyContent="start"
        >
          <Button
            colorScheme="kbuttonblue"
            isLoading={createFieldDefinition.isPending}
            type="submit"
          >
            Save
          </Button>
        </ModalFooter>
      </Box>
      <ModalCloseButton />
    </ModalContent>
  );
};

const IntentSourceTypeRow = () => {
  const {control, setValue, watch} = useIntentFieldForm();
  const group = watch('group');
  const intentGroups = useIntentGroups();

  const intentGroupOptions = useMemo(
    () =>
      intentGroups
        .map((group) => ({
          value: group,
          label: IntentGroupLabels[group],
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [intentGroups]
  );

  return (
    <>
      <GridItem fontSize="sm" textColor="kgray.300" textTransform="uppercase">
        Source Type
      </GridItem>
      <GridItem>
        <Controller
          name="group"
          control={control}
          rules={{required: true}}
          render={({field, fieldState: {error}}) => (
            <Select
              {...field}
              onChange={(newValue) => {
                field.onChange(newValue?.value);
                // Reset other fields when source type changes
                setValue('adAccountId', undefined);
                setValue('linkedInField', undefined);
                setValue('interval', undefined);
                setValue('sourceId', undefined);
                setValue('fieldLabel', '');
              }}
              options={intentGroupOptions}
              placeholder="Select source type"
              value={
                intentGroupOptions.find((option) => option.value === group) ??
                null
              }
              styles={errorSelectStyles<
                (typeof intentGroupOptions)[number],
                false
              >(!!error)}
            />
          )}
        />
      </GridItem>
    </>
  );
};

const linkedInFieldOptions = LinkedInIntentFieldTypes.map((fieldType) => ({
  value: fieldType,
  label: LinkedInFieldTypeLabels[fieldType],
}));

const LinkedInFieldRow = () => {
  const {control, watch} = useIntentFieldForm();
  const linkedInField = watch('linkedInField');

  return (
    <>
      <GridItem fontSize="sm" textColor="kgray.300" textTransform="uppercase">
        LinkedIn Field
      </GridItem>
      <GridItem>
        <Controller
          name="linkedInField"
          control={control}
          rules={{required: true}}
          render={({field, fieldState: {error}}) => (
            <Select
              {...field}
              onChange={(newValue) => {
                field.onChange(newValue?.value);
              }}
              options={linkedInFieldOptions}
              placeholder="Select LinkedIn field type"
              value={
                linkedInFieldOptions.find(
                  (option) => option.value === linkedInField
                ) ?? null
              }
              styles={errorSelectStyles<
                (typeof linkedInFieldOptions)[number],
                false
              >(!!error)}
            />
          )}
        />
      </GridItem>
    </>
  );
};

const LinkedInAdAccountRow = () => {
  const adAccounts = useGetAdAccounts({});
  const adAccountOptions = (adAccounts.data ?? []).map((account) => ({
    value: account.id,
    label: account.name,
  }));

  const {control, watch} = useIntentFieldForm();
  const adAccountId = watch('adAccountId');

  return (
    <>
      <GridItem fontSize="sm" textColor="kgray.300" textTransform="uppercase">
        Ad Account
      </GridItem>
      <GridItem>
        <Controller
          name="adAccountId"
          control={control}
          rules={{required: true}}
          render={({field, fieldState: {error}}) => (
            <Select
              {...field}
              isLoading={adAccounts.isPending}
              onChange={(newValue) => {
                field.onChange(newValue?.value);
              }}
              options={adAccountOptions}
              placeholder="Select ad account"
              value={
                adAccountOptions.find(
                  (option) => option.value === adAccountId
                ) ?? null
              }
              styles={errorSelectStyles<
                (typeof adAccountOptions)[number],
                false
              >(!!error)}
            />
          )}
        />
      </GridItem>
    </>
  );
};

const IntentSourceRow = () => {
  const {control, watch} = useIntentFieldForm();
  const group = watch('group');

  const customer = useCustomer();
  const intentSourceOptions = useMemo(
    () =>
      (customer.intentSources ?? [])
        .filter(({type}) => type === group)
        .map(({id, label}) => ({
          value: id,
          label,
        })),
    [customer.intentSources, group]
  );

  return (
    <>
      <GridItem fontSize="sm" textColor="kgray.300" textTransform="uppercase">
        Intent Source
      </GridItem>
      <GridItem>
        <Controller
          name="sourceId"
          control={control}
          rules={{required: true}}
          render={({field, fieldState: {error}}) => (
            <Select
              {...field}
              onChange={(newValue) => {
                field.onChange(newValue?.value);
              }}
              options={intentSourceOptions}
              placeholder="Select intent source"
              value={
                intentSourceOptions.find(({value}) =>
                  _.isEqual(value, field.value)
                ) ?? null
              }
              styles={errorSelectStyles<
                (typeof intentSourceOptions)[number],
                false
              >(!!error)}
            />
          )}
        />
      </GridItem>
    </>
  );
};

const intervalOptions = ([30, 90, 180] satisfies IntentFieldInterval[]).map(
  (days) => ({
    value: days,
    label: `${days} days`,
  })
);

const IntervalRow = () => {
  const {control, watch} = useIntentFieldForm();
  const interval = watch('interval');

  return (
    <>
      <GridItem fontSize="sm" textColor="kgray.300" textTransform="uppercase">
        Interval
      </GridItem>
      <GridItem>
        <Controller
          name="interval"
          control={control}
          rules={{required: true}}
          render={({field, fieldState: {error}}) => (
            <Select
              {...field}
              onChange={(newValue) => {
                field.onChange(newValue?.value);
              }}
              options={intervalOptions}
              placeholder="Select interval"
              value={
                intervalOptions.find(({value}) => value === interval) ?? null
              }
              styles={errorSelectStyles<
                (typeof intervalOptions)[number],
                false
              >(!!error)}
            />
          )}
        />
      </GridItem>
    </>
  );
};

const FieldLabelRow = () => {
  const {register} = useIntentFieldForm();

  return (
    <>
      <GridItem fontSize="sm" textColor="kgray.300" textTransform="uppercase">
        Field Label
      </GridItem>
      <GridItem>
        <Input
          {...register('fieldLabel', {
            required: true,
          })}
        />
      </GridItem>
    </>
  );
};
